import { LoanApplicationOfferResponse } from "@interfaces/MarketPlace";
import { OfertaCardData } from "@components/ui_v2";
import { formatCurrency } from "@helpers/funciones";

export type ClaveComparativa = "monto" | "tasa" | "tiempo" | "garantia" | "comision" | "plazo";
/** Claves por las que se puede ordenar; `""` es el orden de relevancia que devuelve el backend. */
export type ClaveOrden = "" | ClaveComparativa;

/**
 * Preoferta lista para pintar: la respuesta del API más lo que consumen la tarjeta, los
 * ordenamientos y la vista comparar. Se arma una sola vez por oferta (`toVista`).
 */
export interface OfertaVista {
  offer: LoanApplicationOfferResponse;
  card: OfertaCardData;
  /** Valores numéricos para ordenar y para marcar el mejor de cada categoría. */
  orden: { monto: number; tasa: number; tiempo: number; comision: number; plazo: number; conGarantia: boolean };
  /** Celdas de la vista comparar. */
  lista: Record<ClaveComparativa, string>;
}

export const COLUMNAS: { key: ClaveComparativa; label: string }[] = [
  { key: "monto", label: "Monto" },
  { key: "tasa", label: "Tasa anual" },
  { key: "tiempo", label: "Tiempo" },
  { key: "garantia", label: "Garantía" },
  { key: "comision", label: "Comisiones" },
  { key: "plazo", label: "Plazo" },
];

/**
 * Traduce la oferta del API al modelo que consumen las pantallas del marketplace
 * (`Ofertas` y `OfertaSeleccionada`). Mismos campos y mismos respaldos que la tarjeta
 * legacy (`CreditOfferCard`) y la tabla legacy (`TablaOfertas`).
 */
export const toVista = (offer: LoanApplicationOfferResponse): OfertaVista => {
  const detalles = offer.details;
  const financiera = offer.financial_company?.name || "Financiera";
  const esPartner = offer.financial_company.is_partner;
  const conGarantia = Boolean(detalles?.withGuarantee);
  const garantia = conGarantia ? detalles?.guaranteeDetails || "" : "Ninguna";
  const monto = detalles?.maxAuthorizedAmount || 0;
  const tasa = detalles?.interestRate || 0;
  const tiempo = detalles?.formalizationTime || 0;
  const comision = detalles?.openingFee || 0;
  const plazoMin = detalles?.minAuthorizedPeriod || 0;
  const plazoMax = detalles?.maxAuthorizedPeriod || 0;

  // El legacy distingue la formalización por `is_partner` (dentro de la plataforma de la
  // financiera vía Lendia) y agrega las condiciones especiales cuando vienen.
  const notas = [
    esPartner
      ? `Formalización digital mediante la plataforma ${financiera} - Lendia.`
      : "Formalización a través de la financiera.",
    ...(detalles?.specialConditions ? [detalles.specialConditions] : []),
  ];

  return {
    offer,
    card: {
      id: offer.id,
      financiera,
      logo: offer.financial_company?.logo
        ? `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${offer.financial_company.logo}`
        : undefined,
      digital: esPartner,
      producto: detalles?.creditType || "Crédito Sin Especificar",
      conGarantia,
      vigenciaDias: detalles?.daysValidity || offer.days_validity,
      stats: [
        { label: "Monto Máximo", value: `${formatCurrency(monto)} MXN` },
        { label: "Tasa anual", value: `${tasa}% anual` },
        { label: "Tiempo Formalización", value: `${tiempo} días` },
        { label: "Garantía", value: garantia },
        { label: "Comisiones", value: `${comision}%`, tooltip: "Comisión por apertura" },
        { label: "Plazo", value: `${plazoMin} a ${plazoMax} meses` },
      ],
      notaTitulo: "Preoferta sujeta a",
      notas,
    },
    orden: { monto, tasa, tiempo, comision, plazo: plazoMax, conGarantia },
    lista: {
      monto: formatCurrency(monto),
      tasa: `${tasa}%`,
      tiempo: `${tiempo} días`,
      garantia,
      comision: `${comision}%`,
      plazo: `${plazoMin} a ${plazoMax} meses`,
    },
  };
};
